import BigNumber from 'bignumber.js';
import {
  CommonFractionDigits,
  MarketCapFractionDigits,
} from '@/lib/config/number';

const subscriptNumbers = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];

function numberToSubscriptNumber(number: number): string {
  const str = number.toString();
  let subscriptNumber = '';
  for (let i = 0; i < str.length; i++) {
    subscriptNumber += subscriptNumbers[+str[i]];
  }
  return subscriptNumber;
}

/**
 * 处理tokenPrice
 * @param price
 * @returns
 */
export function formatPrice(price?: string | number, fixed?: number): string {
  // 处理无效输入
  if (price === undefined || price === null || price === '') return '-';

  try {
    // 如果是数字，先控制精度
    if (typeof price === 'number') price = price.toFixed(20);
    const bn = new BigNumber(price);
    if (bn.isNaN() || bn.isZero()) return '0';

    const abs = bn.abs();

    // 处理大于等于 1 的价格
    if (abs.gte(1)) return abs.toFixed(2);

    // 处理小于 1 的价格
    if (abs.lt(0.0001)) {
      const str = abs.toFixed(20);
      const match = str.match(/^0\.(0*)[1-9]/);
      if (match) {
        const zerosCount = match[1].length;
        const significantPart = str.slice(
          match[0].length - 1,
          match[0].length + 3,
        );
        return `0.0{${zerosCount}}${significantPart}`;
      }
    }

    // 对于一般小数（0.00001 到 1 之间），保留 6 位小数
    return abs.toFixed(fixed ?? 6);
  } catch (error) {
    console.error('Price format error:', error);
    return '0';
  }
}

export function formatCompact(
  number: number,
  maximumFractionDigits?: number,
): string {
  return Intl.NumberFormat(
    'en-US',
    // experimental props
    {
      notation: 'compact',
      roundingMode: 'floor',
      maximumFractionDigits: maximumFractionDigits ?? 1,
    },
  ).format(number);
}

export function commonNumberFormat(number: string | number) {
  // Handle undefined, null, NaN, or empty string
  if (number === undefined || number === null || number === '' || 
      (typeof number === 'number' && isNaN(number))) {
    return '0';
  }
  
  const numStr = String(number);
  
  if (
    numStr.split('.')[1]?.length <= CommonFractionDigits ||
    numStr.split('.')[1]?.length === undefined
  ) {
    return numStr;
  }
  if (typeof number === 'string') {
    const parsed = parseFloat(number);
    return isNaN(parsed) ? '0' : parsed.toFixed(CommonFractionDigits);
  }
  return number.toFixed(CommonFractionDigits);
}

/**
 * 处理市值与流动性
 * @param number
 * @returns
 */
export function marketCapValueFormat(number: number | string): string {
  if (typeof number === 'string') {
    return '$' + formatCompact(parseFloat(number), MarketCapFractionDigits);
  }
  return '$' + formatCompact(number, MarketCapFractionDigits);
}

export function abbreviateHashString(hash: string): string {
  return hash.slice(0, 4) + '...' + hash.slice(-4);
}

export function formatPercentage(percentage: number, needSign = true): string {
  if (percentage >= 0 && needSign) {
    return '+' + percentage.toFixed(2) + '%';
  }
  return percentage.toFixed(2) + '%';
}

export function formatUsdAmount(balance?: any): string {
  if (balance === undefined || balance === null || balance === '') return '-';

  const displayBalance = new BigNumber(balance.toString());
  if (displayBalance.isNaN() || !displayBalance.isFinite()) {
    return '-';
  }
  const fixed = displayBalance.abs().lt(1_000_000) ? 2 : 0;
  return displayBalance.toNumber().toLocaleString(undefined, {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed,
  });
}

// eslint-disable-next-line default-param-last
export function formatAmount(
  balance?: any,
  decimals = 0,
  fixed?: number,
): string {
  // 处理无效输入
  if (balance === undefined || balance === null || balance === '') return '-';

  const bn = new BigNumber(balance.toString());
  if (bn.isNaN() || !bn.isFinite()) return '-';

  const displayBalance = bn.div(new BigNumber(10).pow(decimals));

  if (displayBalance.abs().lt(1000)) {
    if (displayBalance.eq(0)) {
      fixed ??= 2;
    }
    if (displayBalance.abs().lt(1)) {
      fixed ??= 6;
    }
    fixed ??= 2;
    const result = displayBalance.toFixed(fixed, BigNumber.ROUND_FLOOR);
    return result === '0.000000' ? '0.00' : result;
  }

  if (displayBalance.abs().gte(1000000)) {
    fixed ??= 0;
  }

  fixed ??= 2;
  return displayBalance.toNumber().toLocaleString(undefined, {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed,
  });
}

interface FormatPercentOptions {
  prefix?: boolean;
  precision?: number;
  defaultValue?: string;
  max?: number;
  largerSign?: boolean;
}

export function formatPercent(
  number: number | undefined,
  options: FormatPercentOptions = {},
): string {
  const {
    prefix = true,
    precision = 2,
    defaultValue = '--%',
    max = 99.999,
    largerSign = true,
  } = options;

  if (number === undefined) {
    return defaultValue;
  }

  if (number === 0) {
    return '0%';
  }
  const overflow = Math.abs(number) > max;
  const _percent = overflow ? max : number;
  const value = (_percent * 100).toFixed(precision);
  const sign = prefix && _percent > 0 ? '+' : '';
  const largerSignStr = overflow && largerSign ? '>' : '';
  return `${sign}${largerSignStr}${value}%`;
}

export function formatNumber(number: number): string {
  if (number > 1000000000) {
    return `${(number / 1000000000).toFixed(2)}B`;
  } else if (number > 1000000) {
    return `${(number / 1000000).toFixed(2)}M`;
  } else if (number > 1000) {
    return `${(number / 1000).toFixed(2)}K`;
  }
  return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Calculate allowed decimal precision for limit price input based on current price
 * Rule: reduce one decimal place from current price precision
 * Examples:
 * - BTC price 100000.1 (1 decimal) -> allows 0 decimals (flat number)
 * - ETH price 3001.23 (2 decimals) -> allows 1 decimal
 * - Token price 0.000123 (6 decimals) -> allows 5 decimals
 */
export function getLimitPricePrecision(currentPrice: string | number): number {
  if (!currentPrice || currentPrice === '0' || currentPrice === 0) {
    return 2; // Default to 2 decimal places if no current price
  }

  const priceStr = String(currentPrice);
  const decimalIndex = priceStr.indexOf('.');
  
  if (decimalIndex === -1) {
    // No decimal places in current price, allow 0 decimal places
    return 0;
  }
  
  const currentDecimals = priceStr.length - decimalIndex - 1;
  // Reduce by one decimal place, but minimum 0
  return Math.max(0, currentDecimals - 1);
}

/**
 * Format input value to match the allowed decimal precision
 */
export function formatLimitPriceInput(value: string, precision: number): string {
  if (!value || value === '') return value;
  
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  // If precision is 0, return integer
  if (precision === 0) {
    return Math.floor(num).toString();
  }
  
  // Otherwise, format to the specified precision
  return num.toFixed(precision);
}
export function formatToBn(bn: BigNumber, decimals: number): BigNumber {
  return bn.shiftedBy(decimals);
}
export function parseToBN(
  number: string | number | BigNumber,
  decimals: number,
): BigNumber {
  return new BigNumber(number).shiftedBy(-decimals);
}

export function getDecimalPlaces(num: string) {
  if (!num || !num.includes('.')) return 0;
  return num.split('.')[1].length;
}

/**
 * Round number to specified decimal places
 * @param value - Number to round
 * @param decimals - Number of decimal places
 * @returns Rounded number
 * @deprecated This function will be moved or replaced
 */
export function roundToDecimals(value: number, decimals: number): number {
  const multiplier = 10 ** decimals
  return Math.round(value * multiplier) / multiplier
}

/**
 * Round price to 2 decimal places
 * @param price - Price value to round
 * @returns Price rounded to 2 decimal places
 * @deprecated This function will be moved or replaced
 */
export function roundPrice(price: number): number {
  return roundToDecimals(price, 2)
}

/**
 * Round currency value to 2 decimal places
 * @param value - Currency value to round
 * @returns Currency value rounded to 2 decimal places
 * @deprecated This function will be moved or replaced
 */
export function roundCurrency(value: number): number {
  return roundToDecimals(value, 2)
}

/**
 * Round amount/size to 8 decimal places (standard crypto precision)
 * @param amount - Amount value to round
 * @returns Amount rounded to 8 decimal places
 * @deprecated This function will be moved or replaced
 */
export function roundAmount(amount: number): number {
  return roundToDecimals(amount, 8)
}

/**
 * Round leverage to 2 decimal places
 * @param leverage - Leverage value to round
 * @returns Leverage rounded to 2 decimal places
 * @deprecated This function will be moved or replaced
 */
export function roundLeverage(leverage: number): number {
  return roundToDecimals(leverage, 2)
}

/**
 * Round percentage to 2 decimal places
 * @param percentage - Percentage value to round
 * @returns Percentage rounded to 2 decimal places
 * @deprecated This function will be moved or replaced
 */
export function roundPercentage(percentage: number): number {
  return roundToDecimals(percentage, 2)
}

/**
 * Format number with thousands separators
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
// export function formatNumber(value: number, decimals = 2): string {
//   return value.toLocaleString('en-US', {
//     minimumFractionDigits: decimals,
//     maximumFractionDigits: decimals,
//   })
// }

/**
 * Format currency with dollar sign and thousands separators
 * @param value - Currency value to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string
 * @deprecated This function will be moved or replaced
 */
export function formatCurrency(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Format percentage with % symbol
 * @param value - Decimal percentage value (e.g., 0.15 for 15%)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted percentage string
 */
// export function formatPercentage(value: number, decimals = 2): string {
//   return `${roundToDecimals(value * 100, decimals)}%`
// }

/**
 * Safe division with fallback value
 * @param numerator - Numerator value
 * @param denominator - Denominator value
 * @param fallback - Fallback value if denominator is 0 (default: 0)
 * @returns Division result or fallback value
 * @deprecated This function will be moved or replaced
 */
export function safeDivide(numerator: number, denominator: number, fallback = 0): number {
  return denominator === 0 ? fallback : numerator / denominator
}

/**
 * Clamp number between min and max values
 * @param value - Value to clamp
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns Clamped value
 * @deprecated This function will be moved or replaced
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Check if two numbers are approximately equal (within tolerance)
 * @param a - First number
 * @param b - Second number
 * @param tolerance - Tolerance for equality (default: 0.01)
 * @returns True if numbers are approximately equal
 * @deprecated This function will be moved or replaced
 */
export function isApproximatelyEqual(a: number, b: number, tolerance = 0.01): boolean {
  return Math.abs(a - b) < tolerance
}